/**
 * Profil energetyczny klienta: `dane.energia` w karcie klienta (plan PLAN_PROFIL_ENERGETYCZNY_KLIENTA.md).
 *
 * Jedno miejsce na to, co o zużyciu i rozliczeniu energii wie karta, Projekt 3D, asystent i oferta.
 * Pole żyje w JSON-ie `dane` klienta (bez migracji), więc wszystko, co do niego trafia, przechodzi przez
 * `normalizujEnergie`: tylko znane pola, liczby w rozsądnych granicach, krótkie teksty, ograniczona liczba
 * odbiorników. Dzięki temu profil zawsze mieści się w kilku kilobajtach, daleko od limitu 64 KiB na całe
 * `dane` (patrz `LIMIT_DANYCH_B` w `lib/projekt3d/klient3d.js`).
 *
 * Kształt (wszystkie pola opcjonalne, brak to `null`, nie zero):
 *   zuzycie: { sposob: 'kwh'|'rachunek'|'osoby', kwh, rachunekZl, osoby, miesiace: [12]|null,
 *              dodatkiWliczone, zrodlo, data }
 *     `kwh` roczne zużycie domu, `rachunekZl` miesięczny rachunek, `osoby` 1–6 (jak w kalkulatorze mocy).
 *     `miesiace` 12 wartości z faktur, gdy są (dziś tylko do sumy, gdy brak `kwh`).
 *     `dodatkiWliczone`: podane zużycie ZAWIERA już odbiorniki z `dodatki` (np. zużycie z kalkulatora mocy);
 *     domyślnie nie, czyli odbiorniki są planowane i doliczamy je, jak kalkulator mocy i Projekt 3D.
 *     `zrodlo` skąd liczba (np. 'karta', '3d', 'oferta'), `data` dzień wpisu 'RRRR-MM-DD'.
 *   profil: klucz z `PROFILE` (`staly`, `klasyczny`, `wieczorny`)
 *   dodatki: [{ key, kwh, label? }] odbiorniki z `DODATKI` (pompa ciepła, auto elektryczne, bojler…)
 *   taryfa: 'G11'|'G12'|'G12w'|'nie_wiem' (null: nie podano; oba liczymy jak G11, `config/taryfy.js`)
 *   cenaZakupu: zł/kWh brutto z dystrybucją z rachunku klienta: w G11 jedyna, w G12 i G12w strefy droższej
 *   cenaZakupuTania: w G12 i G12w cena strefy tańszej; bez cen klienta liczymy domyślne ceny taryfy
 *   cenaSprzedazy: wartość kWh oddanego w depozycie (z 1,23), gdy klient ją zna (`config/cenyEnergii.js`)
 *   systemRozliczen: 'rce' (instalacje od lipca 2024 r., domyślnie) albo 'rcem' (starsze)
 *   wzrostCen: ułamek rocznie (0,03 = 3%)
 *   ulga: 'brak'|'12'|'19'|'32' (stawka podatku przy uldze termomodernizacyjnej), dotacjaZl
 *   lokalizacja: { wojewodztwo: klucz kalkulatorów ('lodzkie'; poza Polską okręg 'ro_cluj'), lat, lon }
 *   ustawienie, zacienienie: klucze z `ORIENTACJE` i `ZACIENIENIA` (gdy nie ma Projektu 3D)
 *   produkcja: { zrodlo: '3d'|'tabela', kwp, miesiaceKwh: [12], data } albo null
 */

import {
  DODATKI, MAX_OSOB, ORIENTACJE, PROFILE, ZACIENIENIA, obliczMoc, zuzycieRoczne,
} from '../mocKalkulator'
import { SRODKI_WOJEWODZTW } from '../projekt3d/uzysk'
import { okregRo } from '../regionyRumunii'
import { bajty } from '../projekt3d/klient3d'
import { kluczTaryfy } from '../rozliczenie'
import { SYSTEMY_ROZLICZEN } from '../../config/cenyEnergii'

/* ═══ Województwo ═══════════════════════════════════════════════════════════════════════════ */

/** Klucze województw używane przez kalkulatory (`pvgisPolska.js`, `SRODKI_WOJEWODZTW`). */
export const KLUCZE_WOJEWODZTW = Object.keys(SRODKI_WOJEWODZTW)

const BEZ_OGONKOW = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' }

// Słowa bez polskich znaków i wielkości liter; wszystko poza literami rozdziela słowa.
// NFC skleja litery zapisane jako znak plus ogonek, żeby mapa ogonków je rozpoznała.
const slowa = (tekst) => String(tekst)
  .normalize('NFC')
  .toLowerCase()
  .replace(/[ąćęłńóśźż]/g, (z) => BEZ_OGONKOW[z])
  .split(/[^a-z]+/)
  .filter(Boolean)

// Klucz bez łącznika, a sklejamy do trzech kolejnych słów tekstu: „Kujawsko - Pomorskie”,
// „warmińsko mazurskie” i „zachodnio-pomorskie” trafiają w swoje województwa. Najdłuższe
// najpierw, bo „pomorskie” jest też drugim słowem dwóch innych nazw.
const SKLEJONE_KLUCZE = KLUCZE_WOJEWODZTW
  .map((klucz) => ({ klucz, sklejony: klucz.replace(/-/g, '') }))
  .sort((a, b) => b.sklejony.length - a.sklejony.length)

/**
 * Klucz województwa z nazwy wpisanej przez człowieka albo zwróconej przez usługę:
 * „Łódzkie”, „łódzkie”, „woj. łódzkie”, „województwo łódzkie”, „gmina Ozorków, woj. łódzkie”
 * → 'lodzkie'. Nazwa, której nie rozpoznajemy, daje `null`, a nie zgadywanie.
 */
export const kluczWojewodztwa = (nazwa) => {
  if (nazwa == null || nazwa === '') return null
  const s = slowa(nazwa)
  const ciagi = new Set()
  for (let i = 0; i < s.length; i += 1) {
    let ciag = ''
    for (let j = i; j < Math.min(s.length, i + 3); j += 1) {
      ciag += s[j]
      ciagi.add(ciag)
    }
  }
  return SKLEJONE_KLUCZE.find(({ sklejony }) => ciagi.has(sklejony))?.klucz || null
}

/**
 * Najdalej od środka województwa, w stopniach szerokości (ok. 330 km). Dalej to już nie Polska
 * i klucz byłby zmyślony.
 */
const MAKS_ODLEGLOSC_STOPNIE = 3

/**
 * Województwo z najbliższego środka (`SRODKI_WOJEWODZTW`, te same punkty co tabela PVGIS),
 * gdy nazwy nie znamy. To przybliżenie: przy granicy województw potrafi wskazać sąsiednie
 * (np. Radom wychodzi świętokrzyski), więc nazwa z wyszukiwarki ma zawsze pierwszeństwo.
 */
export const wojewodztwoZeWspolrzednych = (lat, lon) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  const kos = Math.cos((lat * Math.PI) / 180)
  let najblizsze = null
  Object.entries(SRODKI_WOJEWODZTW).forEach(([klucz, [la, lo]]) => {
    const d2 = (lat - la) ** 2 + ((lon - lo) * kos) ** 2
    if (!najblizsze || d2 < najblizsze.d2) najblizsze = { klucz, d2 }
  })
  return najblizsze.d2 <= MAKS_ODLEGLOSC_STOPNIE ** 2 ? najblizsze.klucz : null
}

/** Województwo projektu: najpierw nazwa (np. z GUGiK), potem współrzędne, inaczej `null`. */
export const wojewodztwoProjektu = ({ nazwa, lat, lon } = {}) =>
  kluczWojewodztwa(nazwa) || wojewodztwoZeWspolrzednych(lat, lon)

/**
 * Klucz miejsca do kalkulatorów z dowolnego zapisu: polska nazwa albo klucz województwa,
 * a poza Polską klucz okręgu (`ro_cluj`, patrz `lib/regionyRumunii`). Nieznane: `null`.
 */
export const kluczMiejsca = (wartosc) => kluczWojewodztwa(wartosc)
  || (typeof wartosc === 'string' && okregRo(wartosc) ? wartosc : null)

/** Województwo z adresu karty klienta (`dane.adres.wojewodztwo`, tekst wpisany ręcznie albo z GUGiK). */
export const wojewodztwoZAdresu = (adres) => kluczWojewodztwa(adres?.wojewodztwo)

/* ═══ Normalizacja ═════════════════════════════════════════════════════════════════════════ */

export const SPOSOBY_ZUZYCIA = ['kwh', 'rachunek', 'osoby']
export const STAWKI_ULGI = ['brak', '12', '19', '32']
export const ZRODLA_PRODUKCJI = ['3d', 'tabela']
/** Najwięcej odbiorników w profilu; w kalkulatorze mocy jest ich pięć rodzajów. */
export const MAKS_DODATKOW = 10
/** Twardy sufit rozmiaru profilu w bajtach JSON (UTF-8). Normalny profil ma kilkaset bajtów. */
export const LIMIT_ENERGII_B = 4 * 1024
const MAKS_TEKSTU = 40

export const DOMYSLNA_ENERGIA = Object.freeze({
  zuzycie: Object.freeze({
    sposob: 'kwh', kwh: null, rachunekZl: null, osoby: null, miesiace: null,
    dodatkiWliczone: false, zrodlo: null, data: null,
  }),
  profil: 'klasyczny',
  dodatki: Object.freeze([]),
  taryfa: null,
  cenaZakupu: null,
  cenaZakupuTania: null,
  cenaSprzedazy: null,
  systemRozliczen: null,
  wzrostCen: null,
  ulga: 'brak',
  dotacjaZl: null,
  lokalizacja: Object.freeze({ wojewodztwo: null, lat: null, lon: null }),
  ustawienie: 'poludnie',
  zacienienie: 'brak',
  produkcja: null,
})

/** Liczba z liczby albo tekstu („35 000”, „4,5”) w przedziale [min, max]; inaczej `null`. */
const liczbaW = (v, min, max) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/\s/g, '').replace(',', '.'))
  return Number.isFinite(n) && n >= min && n <= max ? n : null
}
const dodatniaW = (v, max) => {
  const n = liczbaW(v, 0, max)
  return n > 0 ? n : null
}
const zaokr = (v, miejsc = 0) => (v == null ? null : Math.round(v * 10 ** miejsc) / 10 ** miejsc)
const tekst = (v) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, MAKS_TEKSTU) : null)
const data = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null)
const kluczZ = (lista, v, domyslny) => (lista.some((x) => x.key === v) ? v : domyslny)

/** 12 nieujemnych liczb (zaokrąglonych) albo `null`; `choćJedna` wymaga choć jednej dodatniej. */
const dwanascie = (v, max, miejsc, chocJedna = false) => {
  if (!Array.isArray(v) && !ArrayBuffer.isView(v)) return null
  const t = Array.from(v).map((x) => liczbaW(x, 0, max))
  if (t.length !== 12 || t.some((x) => x == null)) return null
  if (chocJedna && !t.some((x) => x > 0)) return null
  return t.map((x) => zaokr(x, miejsc))
}

/** Stawka ulgi z dowolnego zapisu: '12', 12, 'p12', 0.12; nieznana to 'brak'. */
export const stawkaUlgi = (v) => {
  if (v == null) return 'brak'
  const s = String(v).trim().toLowerCase().replace(/^p/, '').replace('%', '')
  if (STAWKI_ULGI.includes(s)) return s
  const n = Number(s.replace(',', '.'))
  if (Number.isFinite(n) && n > 0 && n < 1) return stawkaUlgi(Math.round(n * 100))
  return 'brak'
}

/** Klucz progu ulgi w `oplacalnosc.js` (`PROGI_ULGI`): '12' → 'p12', 'brak' → 'brak'. */
export const kluczUlgi = (ulga) => {
  const s = stawkaUlgi(ulga)
  return s === 'brak' ? 'brak' : `p${s}`
}

const normalizujDodatek = (d) => {
  const klucz = typeof d === 'string' ? d : d?.key
  const wzor = DODATKI.find((x) => x.key === klucz)
  if (!wzor) return null
  const kwh = typeof d === 'string' ? null : dodatniaW(d.kwh, 100000)
  const label = typeof d === 'string' ? null : tekst(d.label)
  return {
    key: klucz,
    kwh: kwh != null ? zaokr(kwh) : wzor.kwh,
    ...(label && (wzor.wlasne || label !== wzor.label) ? { label } : {}),
  }
}

const normalizujProdukcje = (p) => {
  if (!p || typeof p !== 'object') return null
  const miesiaceKwh = dwanascie(p.miesiaceKwh, 1e6, 0, true)
  if (!miesiaceKwh) return null
  return {
    zrodlo: ZRODLA_PRODUKCJI.includes(p.zrodlo) ? p.zrodlo : 'tabela',
    kwp: zaokr(dodatniaW(p.kwp, 1000), 3),
    miesiaceKwh,
    data: data(p.data),
  }
}

/**
 * Profil z danych karty: domyślne pola, walidacja i przycięcie. Funkcja idempotentna, więc wolno
 * ją wołać na już znormalizowanym profilu. Nieznane pola znikają; zła wartość wraca do domyślnej.
 */
export const normalizujEnergie = (raw) => {
  const r = raw && typeof raw === 'object' ? raw : {}
  const z = r.zuzycie && typeof r.zuzycie === 'object' ? r.zuzycie : {}
  const l = r.lokalizacja && typeof r.lokalizacja === 'object' ? r.lokalizacja : {}
  const osoby = liczbaW(z.osoby, 1, MAX_OSOB)

  const energia = {
    zuzycie: {
      sposob: SPOSOBY_ZUZYCIA.includes(z.sposob) ? z.sposob : 'kwh',
      kwh: zaokr(dodatniaW(z.kwh, 1e6)),
      rachunekZl: zaokr(dodatniaW(z.rachunekZl ?? z.rachunek, 1e5), 2),
      osoby: osoby != null ? Math.round(osoby) : null,
      miesiace: dwanascie(z.miesiace, 1e5, 1, true),
      dodatkiWliczone: z.dodatkiWliczone === true,
      zrodlo: tekst(z.zrodlo),
      data: data(z.data),
    },
    profil: kluczZ(PROFILE, r.profil, 'klasyczny'),
    dodatki: (Array.isArray(r.dodatki) ? r.dodatki : []).map(normalizujDodatek).filter(Boolean).slice(0, MAKS_DODATKOW),
    taryfa: kluczTaryfy(r.taryfa),
    cenaZakupu: zaokr(liczbaW(r.cenaZakupu, 0.1, 5), 4),
    cenaZakupuTania: zaokr(liczbaW(r.cenaZakupuTania, 0.1, 5), 4),
    cenaSprzedazy: zaokr(liczbaW(r.cenaSprzedazy, 0, 3), 4),
    systemRozliczen: SYSTEMY_ROZLICZEN.some((x) => x.key === r.systemRozliczen) ? r.systemRozliczen : null,
    wzrostCen: zaokr(liczbaW(r.wzrostCen, 0, 0.2), 4),
    ulga: stawkaUlgi(r.ulga),
    dotacjaZl: zaokr(dodatniaW(r.dotacjaZl, 1e6)),
    lokalizacja: {
      wojewodztwo: kluczMiejsca(l.wojewodztwo),
      lat: zaokr(liczbaW(l.lat, -90, 90), 6),
      lon: zaokr(liczbaW(l.lon, -180, 180), 6),
    },
    ustawienie: kluczZ(ORIENTACJE, r.ustawienie, 'poludnie'),
    zacienienie: kluczZ(ZACIENIENIA, r.zacienienie, 'brak'),
    produkcja: normalizujProdukcje(r.produkcja),
  }

  // Wszystko wyżej jest ograniczone z góry, więc ta pętla w praktyce się nie wykonuje. Zostaje jako
  // bezpiecznik na przyszłe pola: profil nie może zjeść limitu `dane` reszcie karty.
  while (bajty(energia) > LIMIT_ENERGII_B && energia.dodatki.length) energia.dodatki.pop()
  return energia
}

/**
 * Profil klienta z całych `dane` karty: `dane.energia` po normalizacji, a gdy profil nie zna
 * województwa, bierzemy je z adresu karty (`dane.adres.wojewodztwo`).
 */
export const energiaKlienta = (dane) => {
  const e = normalizujEnergie(dane?.energia)
  if (!e.lokalizacja.wojewodztwo) e.lokalizacja.wojewodztwo = wojewodztwoZAdresu(dane?.adres)
  return e
}

/* ═══ Wielkości liczone z profilu ════════════════════════════════════════════════════════════ */

/**
 * Roczne zużycie w kWh, razem z planowanymi odbiornikami (ta sama funkcja co w kalkulatorze mocy;
 * rachunek przeliczany po cenie zakupu klienta, gdy ją zna). Bez podstawy (kWh, rachunku albo liczby
 * osób) zwraca 0: same odbiorniki to nie zużycie domu, a zwrot policzony z nich byłby zmyślony.
 */
export const rocznyKwh = (energia) => {
  const e = normalizujEnergie(energia)
  const z = e.zuzycie
  const kwh = z.kwh || (z.miesiace ? z.miesiace.reduce((a, b) => a + b, 0) : null)
  const podstawa = { kwh, rachunek: z.rachunekZl, osoby: z.osoby }[z.sposob]
  if (!podstawa) return 0
  return zuzycieRoczne({
    sposob: z.sposob,
    kwh,
    rachunek: z.rachunekZl,
    osoby: z.osoby,
    dodatki: z.dodatkiWliczone ? [] : e.dodatki,
    cenaKwh: e.cenaZakupu || undefined,
  })
}

/**
 * Moc instalacji z profilu: JEDNA reguła doboru dla całego serwisu, czyli `obliczMoc` z kalkulatora
 * mocy (zużycie przez uzysk kierunku i cienia, razy mnożnik profilu; przy magazynie mniejszy mnożnik).
 * Zwraca pełny wynik `obliczMoc` (`mocKwp`, `liczbaModulow`, `pojemnoscMagazynuKwh`…) albo `null`,
 * gdy profil nie zna zużycia.
 */
export const mocZZuzycia = (energia, { magazyn = false, montaz = 'dach' } = {}) => {
  const e = normalizujEnergie(energia)
  const kwh = rocznyKwh(e)
  if (!kwh) return null
  return obliczMoc({
    sposob: 'kwh',
    kwh,
    dodatki: [],
    montaz,
    orientacja: e.ustawienie,
    zacienienie: e.zacienienie,
    profil: e.profil,
    magazyn,
  })
}

/* ═══ Oferta przy kliencie: profil ↔ odpowiedzi VoltValue Projektu ═══════════════════════════ */

/** Dzień wpisu w profilu (czas lokalny, jak reszta karty). */
const dzisIso = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Suma rocznego zużycia planowanych odbiorników. */
const sumaDodatkow = (e) => e.dodatki.reduce((s, d) => s + (d.kwh || 0), 0)

/**
 * Zużycie domu bez planowanych odbiorników (kWh rocznie) albo 0, gdy profil nie zna podstawy. Do porównań
 * „w karcie X, w projekcie Y”: odbiorniki porównujemy osobno.
 */
export const zuzycieBazoweKwh = (energia) => rocznyKwh({ ...normalizujEnergie(energia), dodatki: [] })

/**
 * Profil klienta w kształcie odpowiedzi VoltValue Projektu (`lib/daneProjektu.js`), z których korzystają
 * ekrany oferty (`SekcjaProdukcji`, `SekcjaOplacalnosci`). To tylko widok na profil: zużycie to CAŁE zużycie
 * z odbiornikami (jak zawsze w tych odpowiedziach), ulga kluczem progu (`p12`), a rodzaj konstrukcji z karty.
 */
export const daneProjektuZEnergii = (energia, { konstrukcja = null } = {}) => {
  const e = normalizujEnergie(energia)
  return {
    wojewodztwo: e.lokalizacja.wojewodztwo,
    ustawienie: e.ustawienie,
    zacienienie: e.zacienienie,
    zuzycieRoczne: rocznyKwh(e),
    profil: e.profil,
    dodatki: e.dodatki,
    kosztOd: null,
    kosztDo: null,
    ulga: kluczUlgi(e.ulga),
    dotacjaZl: e.dotacjaZl || 0,
    wzrostCen: e.wzrostCen,
    ...(konstrukcja ? { konstrukcja } : {}),
  }
}

/** Pola taryfy i cen klienta, które przechodzą z ekranu do profilu bez zmian (normalizuje je `normalizujEnergie`). */
export const POLA_TARYFY = ['taryfa', 'cenaZakupu', 'cenaZakupuTania', 'systemRozliczen']

/**
 * Zmiana z ekranu oferty (`czesc`: pola jak w odpowiedziach VoltValue Projektu) przeniesiona do profilu klienta.
 * Zużycie z oferty to całość z odbiornikami, więc zapisujemy je jako zużycie domu pomniejszone o planowane
 * odbiorniki (gdy odbiorniki są większe od całości, zostają jako wliczone). Zwraca nowy, znormalizowany profil.
 */
export const energiaZEdycjiOferty = (energia, czesc = {}) => {
  const e = normalizujEnergie(energia)
  const w = {
    ...e,
    zuzycie: { ...e.zuzycie },
    lokalizacja: { ...e.lokalizacja },
  }
  if ('zuzycieRoczne' in czesc) {
    const n = dodatniaW(czesc.zuzycieRoczne, 1e6)
    const odbiorniki = e.zuzycie.dodatkiWliczone ? 0 : sumaDodatkow(e)
    const wliczone = n != null && n <= odbiorniki
    w.zuzycie = {
      ...e.zuzycie,
      sposob: 'kwh',
      kwh: n == null ? null : (wliczone ? n : n - odbiorniki),
      miesiace: null,
      dodatkiWliczone: e.zuzycie.dodatkiWliczone || wliczone,
      zrodlo: 'oferta',
      data: dzisIso(),
    }
  }
  if ('profil' in czesc) w.profil = czesc.profil
  if ('ustawienie' in czesc) w.ustawienie = czesc.ustawienie
  if ('zacienienie' in czesc) w.zacienienie = czesc.zacienienie
  if ('wojewodztwo' in czesc) w.lokalizacja.wojewodztwo = czesc.wojewodztwo
  if ('ulga' in czesc) w.ulga = czesc.ulga
  if ('dotacjaZl' in czesc) w.dotacjaZl = czesc.dotacjaZl
  if ('wzrostCen' in czesc) w.wzrostCen = czesc.wzrostCen
  POLA_TARYFY.forEach((k) => { if (k in czesc) w[k] = czesc[k] })
  return normalizujEnergie(w)
}

/** Czy profil ma cokolwiek wpisanego poza wartościami domyślnymi (do decyzji, czy zapisywać `dane.energia`). */
export const energiaJestPusta = (energia) => {
  const e = normalizujEnergie(energia)
  return JSON.stringify(e) === JSON.stringify(DOMYSLNA_ENERGIA)
}
